import { FC, useState, useRef, useEffect } from "react";
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";
import toast from "react-hot-toast";
import { WeekData } from "../types/rentalCarDatesRangeInput.types";
import { getWeeksInMonth, weekContainsUnavailableDates, getAllWeeksForSelection, getSelectedWeeksDateRange } from "../utils/weeksHelper";
import { useData } from "data/data-provider";

interface WeeksViewProps {
  unavailableDates?: Date[];
  partialUnavailability?: { [key: string]: { from: string; to: string }[] };
}

const WeeksView: FC<WeeksViewProps> = ({ 
  unavailableDates = [], 
  partialUnavailability = {} 
}) => {
  const { selectedWeeks, setSelectedWeeks, weeksViewMonth, setWeeksViewMonth, startTime, endTime, s_Time, e_Time, startDate, endDate } =
    useData();
  const weeks = getWeeksInMonth(weeksViewMonth, unavailableDates);
  const currentMonthName = moment(weeksViewMonth).format("MMMM YYYY");
  
  // Calculate times for week display calculations
  const currentStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
  const currentEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;
  
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Recalculate selectedWeeks keys when time changes (interval length changes)
  useEffect(() => {
    if (selectedWeeks.length === 0) return;
    
    const sortedWeeks = [...selectedWeeks].sort();
    const firstKey = sortedWeeks[0];
    const weekCount = sortedWeeks.length;
    
    // Calculate new interval length based on current times
    const startParts = currentStartTime?.split(':').map(Number) || [9, 0];
    const endParts = currentEndTime?.split(':').map(Number) || [17, 0];
    const startTimeInMinutes = (startParts[0] || 0) * 60 + (startParts[1] || 0);
    const endTimeInMinutes = (endParts[0] || 0) * 60 + (endParts[1] || 0);
    const firstWeekDays = endTimeInMinutes > startTimeInMinutes ? 6 : 7;
    
    // Recalculate all keys based on new interval
    const recalculatedKeys: string[] = [];
    let currentIntervalStart = moment(firstKey);
    
    for (let i = 0; i < weekCount; i++) {
      recalculatedKeys.push(currentIntervalStart.format("YYYY-MM-DD"));
      
      // Move to next week's start
      if (i === 0) {
        currentIntervalStart.add(firstWeekDays, 'days');
      } else {
        currentIntervalStart.add(7, 'days');
      }
    }
    
    // Only update if the keys actually changed
    const hasChanged = recalculatedKeys.some((key, idx) => key !== sortedWeeks[idx]);
    if (hasChanged) {
      setSelectedWeeks(recalculatedKeys);
    }
  }, [currentStartTime, currentEndTime]);

  const handleWeekSelect = (week: WeekData) => {
    if (week.isPast) return;
    const hasUnavailableDates = weekContainsUnavailableDates(
      week,
      unavailableDates,
      partialUnavailability,
      false // Don't allow partial availability
    );
    if (hasUnavailableDates) return;
    const nominalKeyDate = week.isCurrentWeek ? week.effectiveStart : week.start;
    const nominalKey = moment(nominalKeyDate).format("YYYY-MM-DD");
    
    setSelectedWeeks((prev) => {
      // Calculate what the actual interval start date would be for this week
      let actualKey = nominalKey;
      
      if (prev.length > 0) {
        // Calculate based on existing selections
        const sortedPrev = [...prev].sort();
        const firstKey = sortedPrev[0];
        
        // Get the time-based interval length
        const startParts = currentStartTime?.split(':').map(Number) || [9, 0];
        const endParts = currentEndTime?.split(':').map(Number) || [17, 0];
        const startTimeInMinutes = (startParts[0] || 0) * 60 + (startParts[1] || 0);
        const endTimeInMinutes = (endParts[0] || 0) * 60 + (endParts[1] || 0);
        const firstWeekDays = endTimeInMinutes > startTimeInMinutes ? 6 : 7;
        
        // Calculate weeks from first based on nominal dates
        const nominalDate = moment(nominalKey);
        const firstDate = moment(firstKey);
        const daysFromFirst = nominalDate.diff(firstDate, 'days');
        const weeksFromFirst = Math.round(daysFromFirst / 7);
        
        // Always calculate actualKey based on interval math for consistency
        if (weeksFromFirst > 0) {
          // Calculate the expected start date for this week position
          const expectedStart = moment(firstKey).add(firstWeekDays + (weeksFromFirst - 1) * 7, 'days');
          actualKey = expectedStart.format("YYYY-MM-DD");
        } else if (weeksFromFirst === 0) {
          // This is the first week (user re-clicked it)
          actualKey = firstKey;
        } else {
          // Week before first - use nominal key
          actualKey = nominalKey;
        }
      }
      
      const isSelected = prev.includes(actualKey);
      let next: string[];
      if (!isSelected) {
        next = [...prev, actualKey].sort();
        if (next.length > 1) {
          const firstKey = next[0];
          const lastKey = next[next.length - 1];
          const firstDate = moment(firstKey);
          const lastDate = moment(lastKey);
          
          const startParts = currentStartTime?.split(':').map(Number) || [9, 0];
          const endParts = currentEndTime?.split(':').map(Number) || [17, 0];
          const startTimeInMinutes = (startParts[0] || 0) * 60 + (startParts[1] || 0);
          const endTimeInMinutes = (endParts[0] || 0) * 60 + (endParts[1] || 0);
          const firstWeekDays = endTimeInMinutes > startTimeInMinutes ? 6 : 7;
          
          const allRequiredKeys: string[] = [];
          let currentIntervalStart = firstDate.clone();
          let weekIndex = 0;
          
          while (currentIntervalStart.isSameOrBefore(lastDate, 'day')) {
            allRequiredKeys.push(currentIntervalStart.format("YYYY-MM-DD"));
            
            if (weekIndex === 0) {
              currentIntervalStart.add(firstWeekDays, 'days');
            } else {
              currentIntervalStart.add(7, 'days');
            }
            weekIndex++;
            
            if (weekIndex > 52) break;
          }
          
          for (const weekKey of allRequiredKeys) {
            const weekStartDate = moment(weekKey);
            const weekEndDate = weekStartDate.clone().add(6, 'days'); // Check 7-day span
            
            let hasUnavailableDate = false;
            for (let d = 0; d <= 6; d++) {
              const checkDate = weekStartDate.clone().add(d, 'days');
              const dateStr = checkDate.format("YYYY-MM-DD");
              
              const isFullyUnavailable = unavailableDates.some(
                unavailableDate => moment(unavailableDate).format("YYYY-MM-DD") === dateStr
              );
              
              const isPartiallyUnavailable = partialUnavailability[dateStr] !== undefined;
              
              if (isFullyUnavailable || isPartiallyUnavailable) {
                hasUnavailableDate = true;
                break;
              }
            }
            
            if (hasUnavailableDate) {
              toast.error("Cannot select weeks with unavailable dates in between. Please select consecutive available weeks.");
              return prev;
            }
          }
        }
      } else {
        next = [...prev].sort();
        if (actualKey === next[0] || actualKey === next[next.length - 1]) {
          next = next.filter((k) => k !== actualKey);
        } else {
          toast.error("You need to unselect the future week(s) first to remove this week from your booking, as this is a long-term booking chain.");
          return prev;
        }
      }
      if (next.length > 1) {
        // Fill in any gaps between first and last selection
        const firstDate = moment(next[0]);
        const lastDate = moment(next[next.length - 1]);
        const filledWeeksSet = new Set<string>(next);
        
        // Calculate interval length
        const startParts = currentStartTime?.split(':').map(Number) || [9, 0];
        const endParts = currentEndTime?.split(':').map(Number) || [17, 0];
        const startTimeInMinutes = (startParts[0] || 0) * 60 + (startParts[1] || 0);
        const endTimeInMinutes = (endParts[0] || 0) * 60 + (endParts[1] || 0);
        const firstWeekDays = endTimeInMinutes > startTimeInMinutes ? 6 : 7;
        
        // Fill gaps by adding calculated interval start dates
        let currentIntervalStart = firstDate.clone();
        let weekCount = 0;
        
        // Only loop while we haven't exceeded the last date
        while (currentIntervalStart.isBefore(lastDate, 'day')) {
          filledWeeksSet.add(currentIntervalStart.format("YYYY-MM-DD"));
          // Move to next week's start
          if (weekCount === 0) {
            currentIntervalStart.add(firstWeekDays, 'days');
          } else {
            currentIntervalStart.add(7, 'days');
          }
          weekCount++;
          
          // Safety check to avoid infinite loop
          if (weekCount > 52) break;
        }
        
        // Add the last date if we haven't already
        if (!filledWeeksSet.has(lastDate.format("YYYY-MM-DD"))) {
          filledWeeksSet.add(lastDate.format("YYYY-MM-DD"));
        }
        
        return Array.from(filledWeeksSet).sort();
      }
      return next;
    });
  };

  const currentMonth = moment().month(); 
  const currentYear = moment().year();
  const monthsInCurrentYear = 12 - currentMonth; // Remaining months in current year
  const availableMonths = Array.from({ length: monthsInCurrentYear }, (_, i) => {
    return moment().add(i, "months").toDate();
  });

  // Get selected weeks display info - uses time-based calculation
  const getSelectedWeeksInfo = () => {
    if (selectedWeeks.length === 0) return null;
    
    // Use actual calculated startDate and endDate from state (set by dateRangeSelection's useEffect)
    if (startDate && endDate) {
      return {
        count: selectedWeeks.length,
        startDate: moment(startDate).format("MMM D, YYYY"),
        endDate: moment(endDate).format("MMM D, YYYY")
      };
    }
    
    // Fallback: calculate using time-based logic
    const allWeeksData = getAllWeeksForSelection(selectedWeeks, unavailableDates);
    const currentStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
    const currentEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;
    
    const range = getSelectedWeeksDateRange(selectedWeeks, allWeeksData, currentStartTime, currentEndTime);
    
    if (range) {
      return {
        count: selectedWeeks.length,
        startDate: moment(range.start).format("MMM D, YYYY"),
        endDate: moment(range.end).format("MMM D, YYYY")
      };
    }
    
    return null;
  };

  const selectedInfo = getSelectedWeeksInfo();

  return (
    <div className="flex flex-col items-center">
      {/* {selectedInfo && (
        <div className="mb-4 px-4 py-3 bg-primary-50 border border-primary-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <CalendarIcon className="w-5 h-5 !text-primary-800" />
            <div className="text-sm">
              <span className="font-semibold text-primary-800">
                {selectedInfo.count} Week{selectedInfo.count > 1 ? 's' : ''} Selected
              </span>
              <span className="text-primary-700 ml-2">
                ({selectedInfo.startDate} - {selectedInfo.endDate})
              </span>
            </div>
          </div>
        </div>
      )} */}
      
      {/* Month Selector */}
      <div className="flex items-center space-x-2 mb-6">
        <button
          onClick={(e) => {
            e.preventDefault();
            const newMonth = moment(weeksViewMonth).subtract(1, "month");
            if (newMonth.isSameOrAfter(moment(), "month")) {
              setWeeksViewMonth(newMonth.toDate());
            }
          }}
          className="p-1 hover:bg-gray-100 rounded-full disabled:opacity-50"
          disabled={moment(weeksViewMonth).isSame(moment(), "month")}
        >
          <ChevronLeftIcon className="w-4 h-4 text-gray-500" />
        </button>
        
        {/* Custom Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={(e) => {
              e.preventDefault();
              setIsDropdownOpen(!isDropdownOpen);
            }}
            className="px-4 py-2 border border-gray-200 rounded-full text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-200 cursor-pointer flex items-center text-center space-x-2 min-w-[160px] justify-center"
          >
            <span className="text-center">{currentMonthName}</span>
            <ChevronDownIcon className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {isDropdownOpen && (
            <div className="absolute top-full left-0 mt-2 w-full min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
              {availableMonths.map((month) => {
                const monthValue = moment(month).format("YYYY-MM");
                const isSelected = monthValue === moment(weeksViewMonth).format("YYYY-MM");
                return (
                  <button
                    key={monthValue}
                    onClick={(e) => {
                      e.preventDefault();
                      const [year, m] = monthValue.split("-");
                      setWeeksViewMonth(new Date(parseInt(year), parseInt(m) - 1, 1));
                      setIsDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-gray-100 font-semibold text-gray-900' : 'text-gray-700'
                    }`}
                  >
                    {moment(month).format("MMMM YYYY")}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        
        <button
          onClick={(e) => {
            e.preventDefault();
            setWeeksViewMonth(moment(weeksViewMonth).add(1, "month").toDate());
          }}
          className="p-1 hover:bg-gray-100 rounded-full"
        >
          <ChevronRightIcon className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {weeks.map((week, index) => {
          const nominalKeyDate = week.isCurrentWeek ? week.effectiveStart : week.start;
          const nominalKey = moment(nominalKeyDate).format("YYYY-MM-DD");
          
          // Calculate what the actual interval key would be for this week
          let actualKey = nominalKey;
          
          if (selectedWeeks.length > 0) {
            const sortedSelected = [...selectedWeeks].sort();
            const firstSelectedKey = sortedSelected[0];
            const firstSelectedDate = moment(firstSelectedKey);
            const nominalDate = moment(nominalKey);
            
            // Calculate weeks difference based on nominal dates
            const daysFromFirst = nominalDate.diff(firstSelectedDate, 'days');
            const weeksFromFirst = Math.round(daysFromFirst / 7);
            
            // Calculate time-based interval  
            const startParts = currentStartTime?.split(':').map(Number) || [9, 0];
            const endParts = currentEndTime?.split(':').map(Number) || [17, 0];
            const startTimeInMinutes = (startParts[0] || 0) * 60 + (startParts[1] || 0);
            const endTimeInMinutes = (endParts[0] || 0) * 60 + (endParts[1] || 0);
            const firstWeekDays = endTimeInMinutes > startTimeInMinutes ? 6 : 7;
            
            // Calculate expected interval start date for this week position
            if (weeksFromFirst > 0) {
              const expectedStart = firstSelectedDate.clone().add(firstWeekDays + (weeksFromFirst - 1) * 7, 'days');
              actualKey = expectedStart.format("YYYY-MM-DD");
            } else if (weeksFromFirst === 0) {
              actualKey = firstSelectedKey;
            }
          }
          
          const isSelected = selectedWeeks.includes(actualKey);
          const hasUnavailableDates = weekContainsUnavailableDates(
            week,
            unavailableDates,
            partialUnavailability,
            false // Don't allow partial availability
          );
          const isDisabled = week.isPast || hasUnavailableDates;
          
          let displayStart: Date;
          let displayEnd: Date;
          
          if (isDisabled) {
            // For past or unavailable weeks, use original dates
            displayStart = week.start;
            displayEnd = week.end;
          } else if (startDate && endDate) {
            // If dates are already saved, use startDate as the reference point
            const savedStartMoment = moment(startDate);
            const thisWeekMoment = moment(nominalKeyDate);
            const daysDiff = thisWeekMoment.diff(savedStartMoment, 'days');
            const weeksDiff = Math.round(daysDiff / 7);
            
            // Calculate the first week's interval days based on times
            const startParts = currentStartTime?.split(':').map(Number) || [9, 0];
            const endParts = currentEndTime?.split(':').map(Number) || [17, 0];
            const startTimeInMinutes = (startParts[0] || 0) * 60 + (startParts[1] || 0);
            const endTimeInMinutes = (endParts[0] || 0) * 60 + (endParts[1] || 0);
            const firstWeekIntervalDays = endTimeInMinutes > startTimeInMinutes ? 6 : 7;
            
            if (weeksDiff === 0) {
              // This is the first week (where startDate falls)
              displayStart = startDate;
              displayEnd = moment(startDate).add(firstWeekIntervalDays, 'days').toDate();
            } else if (weeksDiff > 0) {
              // Weeks after the first week
              displayStart = moment(startDate).add(firstWeekIntervalDays + (weeksDiff - 1) * 7, 'days').toDate();
              displayEnd = moment(displayStart).add(7, 'days').toDate();
            } else {
              // Weeks before the saved start - show original dates
              displayStart = week.start;
              displayEnd = week.end;
            }
          } else if (selectedWeeks.length > 0) {
            // If there are selected weeks (but not yet saved), calculate based on the first SELECTED week
            const sortedSelectedWeeks = [...selectedWeeks].sort();
            const firstSelectedKey = sortedSelectedWeeks[0];
            const allWeeksData = getAllWeeksForSelection(sortedSelectedWeeks, unavailableDates);
            const firstSelectedRange = getSelectedWeeksDateRange([firstSelectedKey], allWeeksData, currentStartTime, currentEndTime);
            
            if (firstSelectedRange) {
              // Find how many weeks this card is from the first selected week
              const firstSelectedDate = moment(firstSelectedKey);
              const thisWeekDate = moment(nominalKey);
              const weeksDiff = Math.round(thisWeekDate.diff(firstSelectedDate, 'days') / 7);
              
              if (weeksDiff === 0) {
                // This is the first selected week
                displayStart = firstSelectedRange.start;
                displayEnd = firstSelectedRange.end;
              } else if (weeksDiff > 0) {
                // Weeks after the first selected week
                displayStart = moment(firstSelectedRange.end).add((weeksDiff - 1) * 7, 'days').toDate();
                displayEnd = moment(displayStart).add(7, 'days').toDate();
              } else {
                // Weeks before the first selected week - just show original dates
                displayStart = week.start;
                displayEnd = week.end;
              }
            } else {
              displayStart = week.start;
              displayEnd = week.end;
            }
          } else {
            // No selection and no saved dates - calculate based on first selectable week in this month view
            const firstSelectableWeekIndex = weeks.findIndex(w => {
              const wHasUnavailableDates = weekContainsUnavailableDates(
                w,
                unavailableDates,
                partialUnavailability,
                false
              );
              return !w.isPast && !wHasUnavailableDates;
            });
            const firstSelectableWeek = firstSelectableWeekIndex >= 0 ? weeks[firstSelectableWeekIndex] : null;
            
            if (firstSelectableWeek) {
              const firstWeekKeyDate = firstSelectableWeek.isCurrentWeek ? firstSelectableWeek.effectiveStart : firstSelectableWeek.start;
              const firstWeekKey = moment(firstWeekKeyDate).format("YYYY-MM-DD");
              const allWeeksData = getAllWeeksForSelection([firstWeekKey], unavailableDates);
              const firstWeekRange = getSelectedWeeksDateRange([firstWeekKey], allWeeksData, currentStartTime, currentEndTime);
              
              if (firstWeekRange) {
                const weeksFromFirst = index - firstSelectableWeekIndex;
                
                if (weeksFromFirst === 0) {
                  displayStart = firstWeekRange.start;
                  displayEnd = firstWeekRange.end;
                } else if (weeksFromFirst > 0) {
                  displayStart = moment(firstWeekRange.end).add((weeksFromFirst - 1) * 7, 'days').toDate();
                  displayEnd = moment(displayStart).add(7, 'days').toDate();
                } else {
                  displayStart = week.start;
                  displayEnd = week.end;
                }
              } else {
                displayStart = week.start;
                displayEnd = week.end;
              }
            } else {
              displayStart = week.start;
              displayEnd = week.end;
            }
          }

          return (
            <button
              key={actualKey}
              onClick={(e) => {
                e.preventDefault();
                handleWeekSelect(week);
              }}
              disabled={isDisabled}
              className={`relative flex flex-col items-center p-4 rounded-2xl border-2 transition-all ${
                isDisabled
                  ? "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
                  : isSelected
                    ? "border-bg-color bg-gray-50"
                    : "border-gray-200 hover:border-gray-300"
              }`}
            >
              {isSelected && (
                <div className="absolute top-1 right-2 z-10 bg-primary-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
               
                </div>
              )}
              <div className="w-12 h-12 flex items-center justify-center mb-2">
                <CalendarIcon
                  className={`w-8 h-8 ${
                    isDisabled ? "text-gray-300" : "text-gray-400"
                  }`}
                />
              </div>
              <span
                className={`text-sm font-semibold ${
                  isDisabled ? "text-gray-400" : "text-gray-800"
                }`}
              >
                Week {week.weekNum}
              </span>
              <span
                className={`text-xs mt-1 ${
                  isDisabled ? "text-gray-300" : "text-gray-500"
                }`}
              >
                {moment(displayStart).format("MMM D")} -{" "}
                {moment(displayEnd).format("MMM D")}
              </span>
              {hasUnavailableDates && !week.isPast && (
                <span className="text-[10px] text-red-500 mt-1">
                  Unavailable
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default WeeksView;
